// Sandbox hosts (docs/plans/2026-10-03-sandbox-hosts-ontwerp.md §11): the
// Docker daemons run sandboxes start on, for the whole installation rather
// than one workspace. Installation administrators only (router.tsx guards the
// route, the API checks every request).
//
// The main server (`local`) always exists; build servers are added over TCP
// + TLS or SSH. The worker checks each host's health and writes it back, so
// "Check now" only queues a check and the list polls for the result.
//
// "Scan & advise" (§16.6) opens the host's form and scans there, so the advice
// can fill its slot fields; adding a host goes straight on to that form.
//
// "Add host with installer" (docs/plans/2026-10-04-sandbox-host-installer.md
// §1) creates a tunnel host and shows the one-line install command; the row
// then walks through waiting, pending approval and connected, and the Agent
// column shows the agent version and its update state.

import { Fragment, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { SandboxHost, SandboxHostInstall } from '@goblic/shared'
import { ApiError, agentPolicyApi, sandboxHostsApi } from '../../lib/api-client'
import { formatRelative } from '../../lib/format'
import { adviceHeadline } from '../../lib/sandbox-host-scan'
import { useDialogs } from '../../components/dialogs'
import { sceneClass } from '../../components/goblin-art'
import {
  AgentApprovalCard,
  agentBadge,
  updateIndicator,
} from '../../components/agent-approval-card'
import { AgentUpdateCard } from '../../components/agent-update-card'
import { InstallCommandPanel } from '../../components/install-command-dialog'
import { SandboxHostForm, TunnelHostForm } from '../../components/sandbox-host-form'
import { StatusBadge } from '../../components/status-badge'
import { Dialog, ErrorNote, LoadingRows, Notice, PageHeader } from '../../components/ui'

const QUERY_KEY = ['sandbox-hosts']
/** The worker answers a queued check within seconds; look again after this. */
const CHECK_REFETCH_MS = 4_000
const STALE_CARD_NOTICE =
  'This host enrolled again since you opened the card. Check the new details before approving.'
const POLICY_KEY = ['agent-policy']
/** While the install dialog is open the list follows the host closely (§1 step 4). */
const INSTALL_REFETCH_MS = 3_000
const IDLE_REFETCH_MS = 15_000

function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback
}

/** `tcp · build-1:2376`, `ssh · goblic@build-1:22`, or "This server". */
function endpointText(host: SandboxHost): string {
  if (host.kind === 'local') return 'This server'
  if (host.kind === 'tunnel') return 'tunnel · installed agent'
  const user = host.kind === 'ssh' && host.sshUser ? `${host.sshUser}@` : ''
  return `${host.kind} · ${user}${host.host ?? ''}${host.port != null ? `:${host.port}` : ''}`
}

function capacityText(host: SandboxHost): string {
  const slots = (value: number | null) => (value == null ? '∞' : String(value))
  return `Heavy ${host.activeRuns.heavy} / ${slots(host.heavySlots)} · Light ${host.activeRuns.light} / ${slots(host.lightSlots)}`
}

function memoryText(bytes: number | null): string | null {
  return bytes == null ? null : `${(bytes / 1024 ** 3).toFixed(1)} GiB`
}

function stateText(host: SandboxHost): string {
  if (!host.enabled) return 'Disabled'
  return host.draining ? 'Draining' : 'Enabled'
}

function activeRunCount(host: SandboxHost): number {
  return host.activeRuns.heavy + host.activeRuns.light
}

export function SandboxHostsPage() {
  const queryClient = useQueryClient()
  const { confirm, alert } = useDialogs()
  const [dialog, setDialog] = useState<
    | { mode: 'add' }
    | { mode: 'edit'; host: SandboxHost; autoScan?: boolean }
    | { mode: 'installer' }
    | { mode: 'install'; hostId: string; install: SandboxHostInstall }
    | null
  >(null)
  /** Set when an approval hit a stale card (409): the host enrolled again since it was shown. */
  const [staleCards, setStaleCards] = useState<Record<string, true>>({})

  const hostsQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => sandboxHostsApi.list(),
    refetchInterval: dialog?.mode === 'install' ? INSTALL_REFETCH_MS : IDLE_REFETCH_MS,
  })
  const hosts = hostsQuery.data?.items ?? []
  const hasTunnelHosts = hosts.some((host) => host.kind === 'tunnel')

  const policyQuery = useQuery({
    queryKey: POLICY_KEY,
    queryFn: () => agentPolicyApi.get(),
    enabled: hasTunnelHosts,
    refetchInterval: IDLE_REFETCH_MS,
  })
  const policy = policyQuery.data

  const refresh = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY })

  const check = useMutation({
    mutationFn: (id: string) => sandboxHostsApi.check(id),
    onSuccess: () => {
      void refresh()
      setTimeout(() => void refresh(), CHECK_REFETCH_MS)
    },
    onError: (error) =>
      void alert({
        kind: 'error',
        title: 'Couldn’t check the host',
        message: errorText(error, ''),
      }),
  })

  const setDraining = useMutation({
    mutationFn: ({ id, draining }: { id: string; draining: boolean }) =>
      sandboxHostsApi.update(id, { draining }),
    onSettled: refresh,
    onError: (error) =>
      void alert({
        kind: 'error',
        title: 'Couldn’t change the host',
        message: errorText(error, ''),
      }),
  })

  const remove = useMutation({
    mutationFn: (id: string) => sandboxHostsApi.remove(id),
    onSettled: refresh,
    onError: (error) =>
      void alert({
        kind: 'error',
        title: 'Couldn’t remove the host',
        message: errorText(error, ''),
      }),
  })

  const setAutoUpdate = useMutation({
    mutationFn: (autoUpdate: boolean) => agentPolicyApi.update(autoUpdate),
    onSettled: () => queryClient.invalidateQueries({ queryKey: POLICY_KEY }),
    onError: (error) =>
      void alert({
        kind: 'error',
        title: 'Couldn’t change automatic updates',
        message: errorText(error, ''),
      }),
  })

  const approve = useMutation({
    mutationFn: ({ id, enrolledAt }: { id: string; enrolledAt: string }) =>
      sandboxHostsApi.approveAgent(id, enrolledAt),
    onSuccess: (_host, { id }) => {
      setStaleCards(({ [id]: _gone, ...rest }) => rest)
      void refresh()
      // The API queues a first check at approval; look for its result soon.
      setTimeout(() => void refresh(), CHECK_REFETCH_MS)
    },
    onError: (error, { id }) => {
      if (error instanceof ApiError && error.status === 409 && error.code === 'enrollment_changed') {
        // The card showed an enrollment that is gone: refetch and show the new facts.
        setStaleCards((current) => ({ ...current, [id]: true }))
        void refresh()
        return
      }
      void alert({ kind: 'error', title: 'Couldn’t approve the host', message: errorText(error, '') })
    },
  })

  const revoke = useMutation({
    mutationFn: (id: string) => sandboxHostsApi.revokeAgent(id),
    onSettled: refresh,
    onError: (error) =>
      void alert({ kind: 'error', title: 'Couldn’t revoke the host', message: errorText(error, '') }),
  })

  const updateNow = useMutation({
    mutationFn: (id: string) => sandboxHostsApi.updateAgent(id),
    onSettled: refresh,
    onError: (error) =>
      void alert({ kind: 'error', title: 'Couldn’t update the agent', message: errorText(error, '') }),
  })

  const installToken = useMutation({
    mutationFn: (id: string) => sandboxHostsApi.installToken(id),
    onSuccess: (install, id) => {
      setDialog({ mode: 'install', hostId: id, install })
      void refresh()
    },
    onError: (error) =>
      void alert({
        kind: 'error',
        title: 'Couldn’t create an install command',
        message: errorText(error, ''),
      }),
  })

  async function handleReject(host: SandboxHost): Promise<void> {
    const ok = await confirm({
      kind: 'delete',
      title: `Reject ${host.name}?`,
      message:
        'The agent is disconnected and its credentials are revoked. Create a new install command to enroll a machine again.',
      confirmLabel: 'Reject',
    })
    if (ok) revoke.mutate(host.id)
  }

  async function handleRevoke(host: SandboxHost): Promise<void> {
    const ok = await confirm({
      kind: 'delete',
      title: `Disconnect and revoke ${host.name}?`,
      message:
        'The agent is disconnected within seconds and can’t reconnect. Running sandboxes on it are lost. Create a new install command to enroll it again.',
      confirmLabel: 'Disconnect and revoke',
    })
    if (ok) revoke.mutate(host.id)
  }

  async function handleRemove(host: SandboxHost): Promise<void> {
    const ok = await confirm({
      kind: 'delete',
      title: `Remove ${host.name}?`,
      message:
        host.kind === 'tunnel'
          ? 'No new sandboxes start there and the agent is disconnected. Sandboxes left on the machine can be cleaned up there first with `sudo goblic-agent uninstall --purge-sandboxes`.'
          : 'No new sandboxes start there. Runs that ran there keep their history; its stored certificates and keys are deleted.',
      confirmLabel: 'Remove',
    })
    if (ok) remove.mutate(host.id)
  }

  /** Why Remove is off for this host, or null when it may go. */
  function removeBlocked(host: SandboxHost): string | null {
    if (host.kind === 'local') return 'The main server can’t be removed; disable it instead.'
    const active = activeRunCount(host)
    if (active > 0)
      return `${active} run${active === 1 ? ' is' : 's are'} still using it. Drain it and wait.`
    return null
  }

  return (
    <div>
      <PageHeader
        title="Sandbox hosts"
        description="Docker hosts where run sandboxes start. Installation-wide."
        actions={
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setDialog({ mode: 'installer' })}
            >
              Add host with installer
            </button>
            <button type="button" className="btn-primary" onClick={() => setDialog({ mode: 'add' })}>
              Add host
            </button>
          </div>
        }
      />

      {hasTunnelHosts && policy ? (
        <div className="mb-4">
          <AgentUpdateCard
            policy={policy}
            saving={setAutoUpdate.isPending}
            onToggle={(autoUpdate) => setAutoUpdate.mutate(autoUpdate)}
          />
        </div>
      ) : null}

      <div className="mb-4">
        <Notice tone="info">
          Sandboxes start on the hosts below. Hosts with a capacity are used first; the main
          server takes the rest unless you disable it.
        </Notice>
      </div>

      <div className={`panel overflow-x-auto ${sceneClass('workshop')}`}>
        {hostsQuery.isLoading ? (
          <LoadingRows />
        ) : hostsQuery.isError ? (
          <ErrorNote error={hostsQuery.error} what="sandbox hosts" />
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Host</th>
                <th>Status</th>
                <th>Capacity</th>
                <th>Docker</th>
                <th>Agent</th>
                <th>State</th>
                <th>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {hosts.map((host) => {
                const checking = check.isPending && check.variables === host.id
                const toggling = setDraining.isPending && setDraining.variables?.id === host.id
                const blocked = removeBlocked(host)
                const agent = host.agent
                const badge = agentBadge(host)
                const connected = agent?.state === 'connected'
                // A tunnel host can only be checked and scanned while its agent is connected.
                const reachable = host.kind !== 'tunnel' || connected
                const indicator = agent ? updateIndicator(agent, policy?.autoUpdate) : null
                const approving = approve.isPending && approve.variables?.id === host.id
                const machine = [
                  host.health.cpus != null
                    ? `${host.health.cpus} CPU${host.health.cpus === 1 ? '' : 's'}`
                    : null,
                  memoryText(host.health.memoryBytes),
                ].filter(Boolean)
                return (
                  <Fragment key={host.id}>
                  <tr>
                    <td>
                      <span className="block font-medium text-ink">{host.name}</span>
                      <span className="block font-mono text-sm text-ink-3">{endpointText(host)}</span>
                      {host.kind === 'tcp' && !host.tlsEnabled ? (
                        <span
                          className="block text-sm text-danger"
                          title="Only inside a private network such as Tailscale or WireGuard."
                        >
                          No TLS
                        </span>
                      ) : null}
                      {host.kind === 'ssh' ? (
                        <span
                          className="block max-w-[16rem] truncate font-mono text-sm text-ink-3"
                          title={host.sshHostFingerprint ?? undefined}
                        >
                          {host.sshHostFingerprint ?? 'Fingerprint recorded on first connection'}
                        </span>
                      ) : null}
                    </td>
                    <td>
                      {badge ? (
                        <StatusBadge status={badge.status} label={badge.label} />
                      ) : (
                        <StatusBadge status={host.health.status} />
                      )}
                      {badge && !connected ? null : host.health.error ? (
                        <span className="mt-1 block max-w-xs text-sm text-danger">
                          {host.health.error}
                        </span>
                      ) : host.health.checkedAt ? (
                        <span className="block text-sm text-ink-3">
                          checked {formatRelative(host.health.checkedAt)}
                        </span>
                      ) : (
                        <span className="block text-sm text-ink-3">Not checked yet</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap tabular-nums">
                      {capacityText(host)}
                      {host.advice ? (
                        <span className="block text-sm text-ink-3">
                          Advice: {adviceHeadline(host.advice)}
                        </span>
                      ) : null}
                    </td>
                    <td>
                      {host.health.dockerVersion ? (
                        <>
                          <span className="block">{host.health.dockerVersion}</span>
                          {machine.length > 0 ? (
                            <span className="block text-sm text-ink-3">{machine.join(' · ')}</span>
                          ) : null}
                        </>
                      ) : (
                        <span className="text-ink-3">–</span>
                      )}
                    </td>
                    <td className="text-sm">
                      {agent && agent.state !== 'waiting' ? (
                        <>
                          <span className="block font-mono text-ink">{agent.version ?? '–'}</span>
                          {agent.lastSeenAt ? (
                            <span className="block text-ink-3">seen {formatRelative(agent.lastSeenAt)}</span>
                          ) : null}
                          {agent.remoteIp ? (
                            <span className="block font-mono text-ink-3">
                              {agent.remoteIp}
                              {agent.remoteIpChangedAt ? (
                                <span
                                  className="ml-1 text-signal"
                                  role="img"
                                  aria-label="IP address changed"
                                  title={`The external IP changed since approval (${formatRelative(agent.remoteIpChangedAt)}).`}
                                >
                                  ⚠
                                </span>
                              ) : null}
                            </span>
                          ) : null}
                          {indicator ? (
                            <span
                              className={`block ${indicator.tone === 'danger' ? 'text-danger' : indicator.tone === 'warning' ? 'text-signal' : 'text-ink-2'}`}
                            >
                              {indicator.text}
                            </span>
                          ) : null}
                        </>
                      ) : (
                        <span className="text-ink-3">–</span>
                      )}
                    </td>
                    <td>
                      <span className={host.enabled && !host.draining ? '' : 'text-ink-3'}>
                        {stateText(host)}
                      </span>
                    </td>
                    <td>
                      <div className="flex justify-end gap-1">
                        <button
                          type="button"
                          className="btn-ghost btn-sm"
                          onClick={() => check.mutate(host.id)}
                          disabled={checking || !reachable}
                        >
                          {checking ? 'Checking…' : 'Check now'}
                        </button>
                        <button
                          type="button"
                          className="btn-ghost btn-sm"
                          onClick={() => setDialog({ mode: 'edit', host, autoScan: true })}
                          disabled={!reachable}
                        >
                          Scan & advise
                        </button>
                        <button
                          type="button"
                          className="btn-ghost btn-sm"
                          onClick={() => setDialog({ mode: 'edit', host })}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn-ghost btn-sm"
                          onClick={() => setDraining.mutate({ id: host.id, draining: !host.draining })}
                          disabled={toggling}
                        >
                          {host.draining ? 'Stop draining' : 'Drain'}
                        </button>
                        {host.kind === 'tunnel' ? (
                          <>
                            <button
                              type="button"
                              className="btn-ghost btn-sm"
                              onClick={() => installToken.mutate(host.id)}
                              disabled={installToken.isPending}
                            >
                              Install command
                            </button>
                            {connected ? (
                              <button
                                type="button"
                                className="btn-ghost btn-sm"
                                onClick={() => updateNow.mutate(host.id)}
                                disabled={updateNow.isPending}
                              >
                                Update agent now
                              </button>
                            ) : null}
                            {agent?.approval === 'approved' ? (
                              <button
                                type="button"
                                className="btn-ghost btn-sm text-danger"
                                onClick={() => void handleRevoke(host)}
                                disabled={revoke.isPending}
                              >
                                Disconnect and revoke
                              </button>
                            ) : null}
                          </>
                        ) : null}
                        <button
                          type="button"
                          className="btn-ghost btn-sm text-danger"
                          onClick={() => void handleRemove(host)}
                          disabled={blocked != null || remove.isPending}
                          title={blocked ?? undefined}
                        >
                          Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                  {agent?.state === 'pending' ? (
                    <tr>
                      <td colSpan={7}>
                        <AgentApprovalCard
                          host={host}
                          busy={approving || revoke.isPending}
                          notice={staleCards[host.id] ? STALE_CARD_NOTICE : null}
                          onApprove={(enrolledAt) => approve.mutate({ id: host.id, enrolledAt })}
                          onReject={() => void handleReject(host)}
                        />
                      </td>
                    </tr>
                  ) : null}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      <p className="mt-4 text-sm text-ink-3">
        Only the sandboxes move: the API, the worker and the database stay on the main server.
        A new host builds its runner images on its first run, which takes a minute or two.
      </p>

      <Dialog
        open={dialog != null}
        onClose={() => setDialog(null)}
        title={
          dialog?.mode === 'edit'
            ? `Edit ${dialog.host.name}`
            : dialog?.mode === 'installer'
              ? 'Add a host with the installer'
              : dialog?.mode === 'install'
                ? 'Install the Goblic agent'
                : 'Add a sandbox host'
        }
        description={
          dialog?.mode === 'installer' || dialog?.mode === 'install'
            ? 'Run one command on the machine. It connects out to Goblic, so it can sit behind NAT.'
            : 'Certificates and keys are stored encrypted and never shown again.'
        }
        art="/goblins/dialog-connect.webp"
        size="lg"
      >
        {dialog?.mode === 'installer' ? (
          <TunnelHostForm
            onDone={({ host, install }) => {
              setDialog({ mode: 'install', hostId: host.id, install })
              void refresh()
            }}
          />
        ) : null}
        {dialog?.mode === 'install' ? (
          (() => {
            const live = hosts.find((host) => host.id === dialog.hostId)
            return live ? (
              <InstallCommandPanel
                host={live}
                install={dialog.install}
                onScan={() => setDialog({ mode: 'edit', host: live, autoScan: true })}
              >
                <AgentApprovalCard
                  host={live}
                  busy={approve.isPending || revoke.isPending}
                  notice={staleCards[live.id] ? STALE_CARD_NOTICE : null}
                  onApprove={(enrolledAt) => approve.mutate({ id: live.id, enrolledAt })}
                  onReject={() => void handleReject(live)}
                />
              </InstallCommandPanel>
            ) : (
              <LoadingRows />
            )
          })()
        ) : null}
        {dialog?.mode === 'add' || dialog?.mode === 'edit' ? (
          <SandboxHostForm
            key={dialog.mode === 'edit' ? `${dialog.host.id}${dialog.autoScan ? ':scan' : ''}` : 'add'}
            existing={dialog.mode === 'edit' ? dialog.host : undefined}
            autoScan={dialog.mode === 'edit' && dialog.autoScan === true}
            onDone={(saved) => {
              // A new host goes on to its own form with a scan running, so the
              // advice can be applied and saved; an edit closes the dialog.
              setDialog(dialog.mode === 'add' ? { mode: 'edit', host: saved, autoScan: true } : null)
              void refresh()
              // The API queues a check too; this one shows "Checking…" and refetches.
              check.mutate(saved.id)
            }}
          />
        ) : null}
      </Dialog>
    </div>
  )
}
